<template>
  <div class="mt-4">
    <h5>Configuración de estampa</h5>
    <p style="color: #4e4e4e; font-size: 0.9rem;">
      Configure cómo se verá su firma electrónica al estamparse en los documentos.
    </p>

    <div class="divider mb-3"></div>

    <!-- Tipo de estampa -->
    <div class="mb-3">
      <label class="font-weight-bold">Tipo de estampa</label>
      <div class="d-flex mt-2">
        <div class="form-check mr-4" v-for="option in typeOptions" :key="option.value">
          <input
            class="form-check-input"
            type="radio"
            :id="`stamp-type-${option.value}`"
            :value="option.value"
            v-model="config.tipo"
          >
          <label class="form-check-label" :for="`stamp-type-${option.value}`">
            {{ option.label }}
          </label>
        </div>
      </div>
    </div>

    <!-- Layout 2 columnas (tipo = imagen generada) -->
    <div v-if="config.tipo === '2'" class="row">
      <!-- Columna izquierda: Editor -->
      <div class="col-md-7">
        <StampTemplateEditor
          v-model="config.plantilla"
          :stampWidth="effectiveAncho"
          :stampHeight="effectiveAlto"
        />

        <!-- Botón rúbrica -->
        <div class="mt-3 mb-3">
          <b-button
            variant="outline-secondary"
            size="sm"
            @click="$refs.rubricaModal.show()"
          >
            <feather-icon icon="EditIcon" size="14" class="mr-1" />
            Configurar rúbrica (firma manuscrita)
          </b-button>
          <span v-if="config.representacion" class="ml-2">
            <img :src="imageFullUrl" alt="Rúbrica" style="max-height: 30px;" class="border rounded" />
          </span>
        </div>
      </div>

      <!-- Columna derecha: Preview -->
      <div class="col-md-5">
        <div class="sticky-top preview-column" :style="previewColumnStyle">
          <!-- Zoom de configuración -->
          <div class="d-flex align-items-center justify-content-end mb-1">
            <label class="small text-muted mb-0 mr-2">Zoom</label>
            <b-form-select
              v-model.number="previewZoom"
              size="sm"
              :options="zoomOptions"
              style="width: auto;"
              v-b-tooltip.hover
              :title="zoomTooltip"
            />
            <feather-icon
              icon="InfoIcon"
              size="14"
              class="ml-1 text-muted"
              v-b-tooltip.hover
              :title="zoomTooltip"
            />
          </div>
          <StampPreviewCanvas
            :template="config.plantilla"
            :representationUrl="representationFullUrl"
            :backgroundUrl="backgroundFullUrl"
            :stampWidth="effectiveAncho"
            :stampHeight="effectiveAlto"
            :zoom="previewZoom"
            :userData="previewUserData"
            :companyData="previewCompanyData"
            @update-position="onElementPositionUpdate"
            @update-size="onElementSizeUpdate"
          />
          <!-- Preview servidor -->
          <div class="mt-3" v-if="serverPreviewUrl">
            <div class="d-flex justify-content-between align-items-center">
              <label class="small font-weight-bold text-muted mb-0">Resultado final</label>
              <span class="badge badge-light border text-muted ml-2" title="Ancho x alto que se está usando">
                {{ effectiveAncho }} × {{ effectiveAlto }}
              </span>
            </div>
            <div class="border rounded p-2 bg-white mt-1 resultado-final-box" :style="resultadoFinalStyle">
              <img :src="serverPreviewUrl" alt="Preview final" class="resultado-final-img" />
            </div>
          </div>
          <b-button
            variant="outline-primary"
            size="sm"
            block
            class="mt-2"
            @click="refreshServerPreview"
            :disabled="loadingPreview"
          >
            <b-spinner small v-if="loadingPreview" class="mr-1" />
            <feather-icon v-else icon="EyeIcon" size="14" class="mr-1" />
            {{ loadingPreview ? 'Generando...' : 'Ver resultado final' }}
          </b-button>
        </div>
      </div>
    </div>

    <!-- Imagen cargada (tipo = imagen cargada) -->
    <div v-if="config.tipo === '1' || config.tipo === '3'" class="mb-4">
      <label class="font-weight-bold">Imagen de la estampa</label>
      <p class="small text-muted mb-2">
        Suba una imagen PNG o JPG que se usará como estampa de firma.
      </p>
      <input
        type="file"
        ref="imageInput"
        accept="image/png,image/jpeg"
        @change="onImageSelected"
        class="form-control-file"
      />
      <div v-if="config.imagen || localImagePreview" class="mt-2">
        <small class="text-muted">Imagen actual:</small>
        <img :src="localImagePreview || imagenCargadaUrl" alt="Estampa cargada" style="max-width: 300px; max-height: 100px; display: block;" class="mt-1 border rounded" />
      </div>
    </div>

    <!-- Dimensiones de la estampa -->
    <div class="mb-3">
      <label class="font-weight-bold">Dimensiones de la estampa</label>
      <p class="small text-muted mb-2">
        Tamaño con el que se dibujará la estampa en el documento. La vista previa
        respeta estas proporciones. Por defecto {{ defaultAncho }} de ancho por
        {{ defaultAlto }} de alto.
      </p>
      <div class="row">
        <div class="col-6 col-md-3">
          <label class="small">Ancho</label>
          <input
            type="number"
            class="form-control form-control-sm"
            v-model.number="config.ancho"
            min="1"
            :placeholder="String(defaultAncho)"
          />
        </div>
        <div class="col-6 col-md-3">
          <label class="small">Alto</label>
          <input
            type="number"
            class="form-control form-control-sm"
            v-model.number="config.alto"
            min="1"
            :placeholder="String(defaultAlto)"
          />
        </div>
      </div>
    </div>

    <!-- Botón guardar -->
    <div class="d-flex justify-content-end mt-3">
      <b-button
        variant="primary"
        @click="saveConfiguration"
        :disabled="saving"
      >
        <b-spinner small v-if="saving" class="mr-1" />
        Guardar configuración
      </b-button>
    </div>

    <!-- Modal rúbrica -->
    <b-modal
      ref="rubricaModal"
      title="Configurar rúbrica (firma manuscrita)"
      hide-footer
      centered
      size="lg"
      @shown="onRubricaModalShown"
    >
      <p class="text-muted small mb-3">
        Esta imagen se superpone a la estampa como representación visual de su firma.
      </p>
      <div class="d-flex mb-3">
        <div class="form-check mr-4">
          <input class="form-check-input" type="radio" id="rep-draw" value="draw" v-model="representationMode">
          <label class="form-check-label" for="rep-draw">Dibujar mi firma</label>
        </div>
        <div class="form-check">
          <input class="form-check-input" type="radio" id="rep-upload" value="upload" v-model="representationMode">
          <label class="form-check-label" for="rep-upload">Subir imagen PNG</label>
        </div>
      </div>
      <div v-show="representationMode === 'draw'">
        <vueSignature
          ref="signaturePadCanvas"
          :key="signaturePadKey"
          class="form-control"
          :sigOption="{ penColor: 'rgb(0, 0, 0)', backgroundColor: 'transparent' }"
          w="100%"
          h="150px"
        />
        <div class="btn-group btn-group-sm mt-2" role="group">
          <button class="btn btn-info btn-sm" @click="clearSignature">Limpiar</button>
          <button class="btn btn-primary btn-sm" @click="saveSignature">Usar</button>
        </div>
      </div>
      <div v-if="representationMode === 'upload'">
        <input
          type="file"
          ref="representationInput"
          accept="image/png,image/jpeg"
          @change="onRepresentationSelected"
          class="form-control-file"
        />
      </div>
      <div v-if="localRepresentationPreview || config.representacion" class="mt-3 text-center">
        <label class="small text-muted">Rúbrica actual:</label><br>
        <img :src="localRepresentationPreview || imageFullUrl" alt="Rúbrica actual" style="max-width: 300px; max-height: 100px;" class="border rounded" />
      </div>
      <div class="text-right mt-3">
        <b-button variant="secondary" size="sm" class="mr-2" @click="$refs.rubricaModal.hide()">Cerrar</b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { signConfigStamp } from '@/febos/servicios/api/usuarios.api';
import {
  ioSaveConfigurations,
  ioGetPrivateUploadUrl,
  ioUploadFileToPrivateUrl
} from '@/febos/servicios/api/herramientas.api';
import { base64ToBlob } from '@/febos/global/utils/files';
import SignaturePad from '@/febos/global/usuario/components/sign/simple/febos/SignaturePad';
import vueSignature from 'vue-signature';
import StampTemplateEditor from './StampTemplateEditor';
import StampPreviewCanvas from './StampPreviewCanvas';

export default {
  name: 'ConfigureSignApiStamp',
  components: { SignaturePad, vueSignature, StampTemplateEditor, StampPreviewCanvas },
  props: {
    prefix: {
      type: String,
      required: true,
      default: 'asignapi'
    },
    user: {
      type: Object,
      required: true
    },
    initialConfig: {
      type: Object,
      default: () => ({})
    },
    customConfigIds: {
      type: Object,
      default: null
    },
    proveedor: {
      type: String,
      default: ''
    },
    tipoFirma: {
      type: String,
      default: 'firmaAvanzada'
    }
  },
  data() {
    return {
      defaultAncho: 150,
      defaultAlto: 50,
      // Escala visual: 1 unidad lógica = previewFactor px de ancho en pantalla.
      // Con factor 1, el ancho en pantalla coincide con el ancho configurado
      // (300 -> 300px), para que el preview sea lo más fiel posible.
      previewFactor: 1,
      // Tope de seguridad para estampas muy anchas / zoom alto; el contenedor
      // además está limitado por max-width: 100% al ancho real de la columna.
      previewMaxWidth: 1200,
      saving: false,
      // Zoom visual del configurador (solo afecta la vista previa editable).
      previewZoom: 1,
      zoomTooltip: 'Amplía la vista solo para configurar la estampa. '
        + 'No afecta el tamaño ni la imagen final.',
      zoomOptions: [
        { value: 1, text: '1x' },
        { value: 2, text: '2x' },
        { value: 3, text: '3x' },
        { value: 4, text: '4x' },
        { value: 5, text: '5x' }
      ],
      previewUrl: '',
      serverPreviewUrl: '',
      loadingPreview: false,
      representationMode: 'draw',
      localRepresentationPreview: null,
      localImagePreview: null,
      signaturePadKey: 0,
      drawnSignatureData: null,
      pendingImageFile: null,
      pendingBackgroundFile: null,
      pendingRepresentationFile: null,
      config: {
        tipo: '2',
        plantilla: '',
        imagen: '',
        background: '',
        representacion: '',
        alto: '',
        ancho: ''
      }
    };
  },
  computed: {
    ...mapGetters('Empresas', ['company']),
    ...mapGetters('Usuario', ['currentUser']),
    // Dimensiones lógicas efectivas de la estampa. Si el usuario no configuró
    // un valor, se usan los defaults del firmador (150 ancho x 50 alto).
    effectiveAncho() {
      const w = parseInt(this.config.ancho, 10);
      return Number.isNaN(w) || w <= 0 ? this.defaultAncho : w;
    },
    effectiveAlto() {
      const h = parseInt(this.config.alto, 10);
      return Number.isNaN(h) || h <= 0 ? this.defaultAlto : h;
    },
    // Ancho en píxeles de la columna de preview, derivado de la config y del
    // zoom del configurador. Escala las unidades lógicas por previewFactor y por
    // el zoom, y lo limita a previewMaxWidth para no desbordar en estampas muy
    // anchas (el contenedor además tiene maxWidth:100%).
    previewColumnWidth() {
      const z = parseInt(this.previewZoom, 10);
      const safeZoom = Number.isNaN(z) || z <= 0 ? 1 : z;
      const scaled = this.effectiveAncho * this.previewFactor * safeZoom;
      return Math.min(scaled, this.previewMaxWidth);
    },
    // Estilo del contenedor de previews: ancho dinámico según la config.
    previewColumnStyle() {
      return {
        top: '20px',
        width: `${this.previewColumnWidth}px`,
        maxWidth: '100%'
      };
    },
    // Relación de aspecto (ancho/alto) tomada de la config, para que el recuadro
    // del "Resultado final" respete la proporción real aunque la imagen no llene
    // toda la caja.
    resultadoFinalStyle() {
      return {
        width: '100%',
        aspectRatio: `${this.effectiveAncho} / ${this.effectiveAlto}`
      };
    },
    configIds() {
      if (this.customConfigIds) return this.customConfigIds;
      const p = this.prefix;
      return {
        tipo: `io.febos.${p}.tipo`,
        plantilla: `io.febos.${p}.plantilla`,
        imagen: `io.febos.${p}.imagen`,
        background: `io.febos.${p}.background`,
        representacion: `io.febos.${p}.representacion`,
        alto: `io.firma.${p}.estampa.al`,
        ancho: `io.firma.${p}.estampa.an`
      };
    },
    proveedorActual() {
      return this.proveedor || this.company.firmaAvanzada || this.company.firmaSimple || '';
    },
    typeOptions() {
      if (this.prefix === 'ssignapi') {
        return [
          { value: '1', label: 'Solo texto (nombre del firmante)' },
          { value: '2', label: 'Imagen generada desde plantilla' },
          { value: '3', label: 'Imagen cargada' }
        ];
      }
      return [
        { value: '2', label: 'Imagen generada desde plantilla' },
        { value: '1', label: 'Imagen cargada' }
      ];
    },
    imageFullUrl() {
      if (!this.config.representacion) return '';
      if (this.config.representacion.startsWith('http')) return this.config.representacion;
      const path = this.config.representacion.startsWith('/') ? this.config.representacion.substring(1) : this.config.representacion;
      return `https://${path}`;
    },
    imagenCargadaUrl() {
      if (!this.config.imagen) return '';
      if (this.config.imagen.startsWith('http')) return this.config.imagen;
      const path = this.config.imagen.startsWith('/') ? this.config.imagen.substring(1) : this.config.imagen;
      return `https://${path}`;
    },
    representationFullUrl() {
      if (this.localRepresentationPreview) return this.localRepresentationPreview;
      return this.imageFullUrl;
    },
    backgroundFullUrl() {
      if (!this.config.background) return '';
      if (this.config.background.startsWith('http')) return this.config.background;
      const path = this.config.background.startsWith('/') ? this.config.background.substring(1) : this.config.background;
      return `https://${path}`;
    },
    previewUserData() {
      return {
        nombre: this.currentUser ? this.currentUser.nombre : 'Nombre Usuario',
        cargo: this.currentUser ? (this.currentUser.cargo || 'Cargo') : 'Cargo',
        iut: this.user ? this.user.iut : '12345678-9',
        correo: this.currentUser ? this.currentUser.correo : 'correo@empresa.cl'
      };
    },
    previewCompanyData() {
      return {
        razonSocial: this.company ? this.company.razonSocial : 'Empresa S.A.'
      };
    }
  },
  methods: {
    onImageSelected(event) {
      const file = event.target.files[0] || null;
      this.pendingImageFile = file;
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => { this.localImagePreview = e.target.result; };
        reader.readAsDataURL(file);
      }
    },
    onBackgroundSelected(event) {
      this.pendingBackgroundFile = event.target.files[0] || null;
    },
    onRepresentationSelected(event) {
      const file = event.target.files[0] || null;
      this.pendingRepresentationFile = file;
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.trimImageAsync(e.target.result).then(trimmed => {
            this.localRepresentationPreview = trimmed;
            this.drawnSignatureData = trimmed;
            this.pendingRepresentationFile = null;
            this.updateSignatureLineSize(trimmed);
          });
        };
        reader.readAsDataURL(file);
      }
    },
    onSignatureDrawn({ png }) {
      this.trimImageAsync(png).then(trimmed => {
        this.drawnSignatureData = trimmed;
        this.localRepresentationPreview = trimmed;
        this.updateSignatureLineSize(trimmed);
      });
    },
    onRubricaModalShown() {
      // Force SignaturePad to re-mount with correct dimensions
      this.signaturePadKey++;
    },
    clearSignature() {
      if (this.$refs.signaturePadCanvas) {
        this.$refs.signaturePadCanvas.clear();
      }
    },
    saveSignature() {
      if (this.$refs.signaturePadCanvas) {
        const png = this.$refs.signaturePadCanvas.save();
        this.onSignatureDrawn({ png });
      }
    },
    updateSignatureLineSize(dataUri) {
      // Calcular dimensiones reales de la imagen y actualizar AX/AY en la plantilla
      const img = new Image();
      img.onload = () => {
        const maxHeight = 25; // AY fijo
        const ratio = img.width / img.height;
        const newAx = Math.round(maxHeight * ratio);
        const newAy = maxHeight;
        // Actualizar la línea T=F en la plantilla
        const lines = (this.config.plantilla || '').split('\n');
        for (let i = 0; i < lines.length; i++) {
          if (lines[i].includes('T=F')) {
            lines[i] = lines[i]
              .replace(/AX=\d+/, `AX=${newAx}`)
              .replace(/AY=\d+/, `AY=${newAy}`);
            break;
          }
        }
        this.config.plantilla = lines.join('\n');
      };
      img.src = dataUri;
    },
    trimImageAsync(dataUri) {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const { data, width, height } = imageData;

            let top = height, left = width, right = 0, bottom = 0;

            for (let y = 0; y < height; y++) {
              for (let x = 0; x < width; x++) {
                const idx = (y * width + x) * 4;
                const alpha = data[idx + 3];
                if (alpha > 10) {
                  if (x < left) left = x;
                  if (x > right) right = x;
                  if (y < top) top = y;
                  if (y > bottom) bottom = y;
                }
              }
            }

            if (right <= left || bottom <= top) { resolve(dataUri); return; }

            const pad = 4;
            left = Math.max(0, left - pad);
            top = Math.max(0, top - pad);
            right = Math.min(width - 1, right + pad);
            bottom = Math.min(height - 1, bottom + pad);

            const trimW = right - left + 1;
            const trimH = bottom - top + 1;
            const trimCanvas = document.createElement('canvas');
            trimCanvas.width = trimW;
            trimCanvas.height = trimH;
            const trimCtx = trimCanvas.getContext('2d');
            trimCtx.drawImage(canvas, left, top, trimW, trimH, 0, 0, trimW, trimH);
            resolve(trimCanvas.toDataURL('image/png'));
          } catch (e) {
            resolve(dataUri);
          }
        };
        img.onerror = () => resolve(dataUri);
        img.src = dataUri;
      });
    },
    onElementPositionUpdate(lineIndex, newX, newY) {
      const lines = (this.config.plantilla || '').split('\n').filter(l => l.trim());
      if (lineIndex < 0 || lineIndex >= lines.length) return;
      const line = lines[lineIndex];
      const split = line.split('||||');
      if (split.length < 2) return;
      let config = split[0].trim();
      const text = split[1];
      config = config.replace(/X=-?\d+/, `X=${newX}`).replace(/Y=-?\d+/, `Y=${newY}`);
      lines[lineIndex] = `${config} |||| ${text.trim()}`;
      this.config.plantilla = lines.join('\n');
    },
    onElementSizeUpdate(lineIndex, newAx, newAy) {
      const lines = (this.config.plantilla || '').split('\n').filter(l => l.trim());
      if (lineIndex < 0 || lineIndex >= lines.length) return;
      const line = lines[lineIndex];
      const split = line.split('||||');
      if (split.length < 2) return;
      let config = split[0].trim();
      const text = split[1];
      config = config.replace(/AX=-?\d+/, `AX=${newAx}`).replace(/AY=-?\d+/, `AY=${newAy}`);
      lines[lineIndex] = `${config} |||| ${text.trim()}`;
      this.config.plantilla = lines.join('\n');
    },
    async uploadFile(file) {
      const ahora = new Date().getTime();
      const ext = file.name.split('.').pop();
      const basePublicUrl = 'febos-io/publicar';
      const url = `/archivos.febos.io/${process.env.VUE_APP_CODIGO_PAIS_S3}/${process.env.VUE_APP_AMBIENTE}/`
        + `usuario/${this.user.iut}/firma/${ahora}.${ext}`;
      const response = await ioGetPrivateUploadUrl(`${basePublicUrl}${url}`, file.type);
      await ioUploadFileToPrivateUrl(response.data?.url, file);
      return url;
    },
    async uploadDrawnSignature(dataUri) {
      const ahora = new Date().getTime();
      const basePublicUrl = 'febos-io/publicar';
      const url = `/archivos.febos.io/${process.env.VUE_APP_CODIGO_PAIS_S3}/${process.env.VUE_APP_AMBIENTE}/`
        + `usuario/${this.user.iut}/firma/${ahora}.png`;
      const base64Content = dataUri.replace(/^data:image\/(png|jpg);base64,/, '');
      const blob = base64ToBlob(base64Content, 'image/png', 'signature.png');
      const response = await ioGetPrivateUploadUrl(`${basePublicUrl}${url}`, 'image/png');
      await ioUploadFileToPrivateUrl(response.data?.url, blob);
      return url;
    },
    async saveConfiguration() {
      this.saving = true;
      try {
        const configurations = [];

        configurations.push({ valor: this.config.tipo, parametroId: this.configIds.tipo });

        if (this.config.plantilla) {
          configurations.push({ valor: this.config.plantilla, parametroId: this.configIds.plantilla });
        }

        if (this.config.alto) {
          configurations.push({ valor: this.config.alto, parametroId: this.configIds.alto });
        }
        if (this.config.ancho) {
          configurations.push({ valor: this.config.ancho, parametroId: this.configIds.ancho });
        }

        // Upload archivos pendientes
        if (this.pendingImageFile) {
          const url = await this.uploadFile(this.pendingImageFile);
          this.config.imagen = url;
          configurations.push({ valor: url, parametroId: this.configIds.imagen });
        }
        if (this.pendingBackgroundFile) {
          const url = await this.uploadFile(this.pendingBackgroundFile);
          this.config.background = url;
          configurations.push({ valor: url, parametroId: this.configIds.background });
        }
        if (this.pendingRepresentationFile) {
          const url = await this.uploadFile(this.pendingRepresentationFile);
          this.config.representacion = url;
          configurations.push({ valor: url, parametroId: this.configIds.representacion });
        } else if (this.drawnSignatureData) {
          const url = await this.uploadDrawnSignature(this.drawnSignatureData);
          this.config.representacion = url;
          configurations.push({ valor: url, parametroId: this.configIds.representacion });
        }

        await ioSaveConfigurations({ origen: 'USUARIO' }, configurations);

        this.$bvToast.toast(
          'Configuración de estampa guardada correctamente',
          { title: 'Firma', variant: 'success', solid: true, autoHideDelay: 3000 }
        );

        this.pendingImageFile = null;
        this.pendingBackgroundFile = null;
        this.pendingRepresentationFile = null;
      } catch (error) {
        this.$bvToast.toast(
          error.response?.data?.errores?.[0] || 'Error al guardar configuración',
          { title: 'Error', variant: 'danger', solid: true, noAutoHide: true }
        );
      } finally {
        this.saving = false;
      }
    },
    async refreshServerPreview() {
      this.loadingPreview = true;
      try {
        const parametros = {
          plantilla: this.config.plantilla,
          backgroundKey: this.configIds.background,
          representacionKey: this.configIds.representacion,
          alto: this.effectiveAlto,
          ancho: this.effectiveAncho
        };
        // Enviar imagen en base64 si hay una nueva sin guardar
        if (this.drawnSignatureData) {
          const base64Only = this.drawnSignatureData.replace(/^data:image\/(png|jpeg);base64,/, '');
          parametros.representacionBase64 = base64Only;
        }

        const response = await signConfigStamp({
          categoria: 'PREVISUALIZAR_PLANTILLA',
          idUsuarioFirma: this.user.id,
          parametros,
          proveedor: this.proveedorActual,
          tipoFirma: this.tipoFirma
        });
        const estampa = response.data?.parametros?.estampa;
        if (estampa) {
          this.serverPreviewUrl = estampa.startsWith('data:') ? estampa : `data:image/png;base64,${estampa}`;
        }
      } catch (error) {
        this.$bvToast.toast(
          'Error al generar vista previa',
          { title: 'Error', variant: 'warning', solid: true }
        );
      } finally {
        this.loadingPreview = false;
      }
    },
    loadInitialConfig() {
      if (this.initialConfig) {
        this.config.tipo = this.initialConfig.tipo || '2';
        this.config.plantilla = this.initialConfig.plantilla || '';
        this.config.imagen = this.initialConfig.imagen || '';
        this.config.background = this.initialConfig.background || '';
        this.config.representacion = this.initialConfig.representacion || '';
        this.config.alto = this.initialConfig.alto || '';
        this.config.ancho = this.initialConfig.ancho || '';
      }
    },
    async loadCurrentConfigurations() {
      try {
        const response = await signConfigStamp({
          categoria: 'OBTENER_CONFIG_ESTAMPA',
          idUsuarioFirma: this.user.id,
          parametros: {
            configuraciones: Object.values(this.configIds)
          },
          proveedor: this.proveedorActual,
          tipoFirma: this.tipoFirma
        });
        const params = response.data && response.data.parametros ? response.data.parametros : {};
        if (params[this.configIds.tipo]) this.config.tipo = params[this.configIds.tipo];
        if (params[this.configIds.plantilla]) this.config.plantilla = params[this.configIds.plantilla];
        if (params[this.configIds.imagen]) this.config.imagen = params[this.configIds.imagen];
        if (params[this.configIds.background]) this.config.background = params[this.configIds.background];
        if (params[this.configIds.representacion]) this.config.representacion = params[this.configIds.representacion];
        if (params[this.configIds.alto]) this.config.alto = params[this.configIds.alto];
        if (params[this.configIds.ancho]) this.config.ancho = params[this.configIds.ancho];
      } catch (error) {
        // Si falla, usamos los defaults
        console.debug('No se pudieron cargar configuraciones de estampa', error);
      }
    }
  },
  async mounted() {
    this.loadInitialConfig();
    await this.loadCurrentConfigurations();
  }
};
</script>

<style scoped>
/* El ancho de la columna de previews se define dinámicamente en
   previewColumnStyle (derivado de la config), de modo que la "Vista previa"
   (canvas) y el "Resultado final" (imagen del servidor) compartan el mismo
   ancho y sean visualmente consistentes con la proporción configurada. */

/* El recuadro del resultado final respeta la proporción de la config
   (aspect-ratio dinámico) y la imagen se ajusta dentro sin deformarse. */
.resultado-final-box {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.resultado-final-img {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  display: block;
}
</style>
